import { MIN_PLAYERS, type MemberInfo, type PlayerActivity, type RevealView, type RoomState } from '@panels/shared';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, closeRoom, joinRoom, revealAction, setReady, startRound, voteSkip } from '../lib/api.js';
import { copyText } from '../lib/export.js';
import { useNow } from '../lib/usePoll.js';
import { useRoomState } from '../lib/useRoomState.js';
import { ErrorNote, Screen, Spinner, StatusDot, type Status } from '../components/ui.js';
import { Reveal } from './Reveal.js';

export function Room() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { state, receivedAt, error, loading, apply, refresh } = useRoomState(roomId);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);

  // Your turn: go straight to the canvas. This is the party-mode loop.
  const nextPanelId = state?.round?.status === 'active' ? state.assignments[0]?.panelId : undefined;
  useEffect(() => {
    if (nextPanelId) navigate(`/room/${roomId}/draw/${nextPanelId}`);
  }, [nextPanelId, roomId, navigate]);

  if (loading && !state) return <Spinner label="Loading room…" />;
  if (!state) {
    return (
      <Screen title="Room" back="/">
        <ErrorNote onRetry={() => void refresh()}>{error ?? 'Could not load that room'}</ErrorNote>
      </Screen>
    );
  }

  // Round 3: after a round, the group watches together by default. Solo
  // browsing — the lobby below — is what's left when co-viewing is off or over.
  if (roomId && state.round?.status !== 'active' && state.reveal?.mode === 'coview') {
    return <Reveal roomId={roomId} state={state} apply={apply} />;
  }

  /** Host only: bring everyone back to watching together. */
  const watchTogether = async () => {
    if (!roomId) return;
    setBusy(true);
    setActionError(null);
    try {
      apply(await revealAction(roomId, { type: 'restart' }));
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not start co-viewing');
    } finally {
      setBusy(false);
    }
  };

  const begin = async () => {
    if (!roomId) return;
    setBusy(true);
    setActionError(null);
    try {
      apply(await startRound(roomId));
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not start the round');
    } finally {
      setBusy(false);
    }
  };

  const ready = async (next: boolean) => {
    if (!roomId) return;
    setBusy(true);
    setActionError(null);
    try {
      apply((await setReady(roomId, next)).state);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not update that');
    } finally {
      setBusy(false);
    }
  };

  /** A skipped player sitting in the lobby gets back in for the next round. */
  const rejoin = async () => {
    if (!state) return;
    setBusy(true);
    setActionError(null);
    try {
      apply(await joinRoom(state.room.code));
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not rejoin');
    } finally {
      setBusy(false);
    }
  };

  const skip = async (targetId: string) => {
    if (!roomId) return;
    setActionError(null);
    try {
      const result = await voteSkip(roomId, targetId);
      apply(result.state);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not send that vote');
    }
  };

  const close = async () => {
    if (!roomId) return;
    setBusy(true);
    setActionError(null);
    try {
      await closeRoom(roomId);
      navigate('/', { replace: true });
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not close the room');
      setBusy(false);
      setClosing(false);
    }
  };

  const roundActive = state.round?.status === 'active';
  const activeMembers = state.members.filter((m) => m.active);
  const isOpen = state.room.status === 'open';
  const canStart = state.you.isCreator && activeMembers.length >= MIN_PLAYERS && !roundActive && isOpen;
  const youReady = state.members.find((m) => m.id === state.you.id)?.ready ?? false;

  return (
    <Screen title={roundActive ? `Panel ${state.round?.phase} of ${state.round?.panelCount}` : 'Lobby'} back="/">
      {error ? <p className="muted small">Reconnecting…</p> : null}
      {actionError ? <ErrorNote>{actionError}</ErrorNote> : null}

      {!isOpen ? (
        <div className="banner">This room is closed. Its comics are still in your gallery.</div>
      ) : null}

      {!roundActive && isOpen ? <RoomCode code={state.room.code} /> : null}

      {roundActive ? (
        <WaitingPanel state={state} receivedAt={receivedAt} onSkip={skip} />
      ) : (
        <>
          {state.reveal && state.reveal.mode !== 'coview' ? (
            <SoloNote reveal={state.reveal} youId={state.you.id} busy={busy} onWatchTogether={watchTogether} />
          ) : null}

          {state.revealReady && state.latestRoundSheets.length > 0 ? (
            <section className="section">
              <h2>Round {state.round?.roundNumber} comics</h2>
              <ul className="list">
                {state.latestRoundSheets.map((comic) => (
                  <li key={comic.sheetId}>
                    {/* The comic page keeps an eye on the room, so a restarted co-view brings everyone back. */}
                    <Link className="row" to={`/comic/${comic.sheetId}`} state={{ revealRoomId: roomId }}>
                      <span className="row-main">
                        <strong>{comic.title ?? `${comic.starter.displayName}'s comic`}</strong>
                        <span className="muted">
                          {comic.status === 'abandoned' ? 'unfinished · ' : ''}
                          {comic.participants.map((p) => p.displayName).join(', ')}
                        </span>
                      </span>
                      <span className="row-chevron">›</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <MemberList
            members={state.members}
            creatorId={state.room.creatorId}
            youId={state.you.id}
            showReady={state.readyUpOpen}
          />

          {isOpen && !state.you.active ? (
            <div className="card card-soft">
              <p className="muted">You were skipped last round. Rejoin to play the next one.</p>
              <button type="button" className="button primary" onClick={rejoin} disabled={busy}>
                Rejoin
              </button>
            </div>
          ) : null}

          {state.readyUpOpen && state.you.active ? (
            <ReadyUp
              members={activeMembers}
              youReady={youReady}
              busy={busy}
              onReady={ready}
            />
          ) : null}

          {!isOpen ? null : state.you.isCreator ? (
            <button
              type="button"
              className={`button ${state.readyUpOpen ? '' : 'primary'}`}
              onClick={begin}
              disabled={!canStart || busy}
            >
              {busy ? 'Starting…' : state.revealReady ? 'Start another round now' : 'Start round'}
            </button>
          ) : state.readyUpOpen ? null : (
            <p className="muted center">
              {activeMembers.length < MIN_PLAYERS
                ? 'Waiting for more players…'
                : `Waiting for ${state.members.find((m) => m.isCreator)?.displayName ?? 'the host'} to start`}
            </p>
          )}
          {isOpen && state.you.isCreator && activeMembers.length < MIN_PLAYERS ? (
            <p className="muted center small">You need at least {MIN_PLAYERS} players.</p>
          ) : null}

          <ShareRoomLink shareToken={state.room.shareToken} />

          {state.you.isCreator && state.room.status === 'open' ? (
            <button type="button" className="link-button block danger" onClick={() => setClosing(true)}>
              Close this room
            </button>
          ) : null}
        </>
      )}

      {closing ? (
        <div className="sheet-backdrop" onClick={() => setClosing(false)}>
          <div className="sheet" onClick={(e) => e.stopPropagation()}>
            <h2>Close this room?</h2>
            <p className="muted">
              {roundActive
                ? 'The round in progress will stop, and any comic nobody finished is kept as unfinished.'
                : 'Nobody will be able to join or start another round here.'}{' '}
              Your comics stay in the gallery and their share links keep working.
            </p>
            <button type="button" className="button primary" onClick={close} disabled={busy}>
              {busy ? 'Closing…' : 'Close the room'}
            </button>
            <button type="button" className="button" onClick={() => setClosing(false)}>
              Keep it open
            </button>
          </div>
        </div>
      ) : null}
    </Screen>
  );
}

/**
 * Solo mode after a round (Round 3): co-viewing was turned off or its driver
 * wandered off, or the show is over. Everyone browses on their own, exactly as
 * before co-viewing existed; the host can bring the group back together.
 */
function SoloNote({
  reveal,
  youId,
  busy,
  onWatchTogether,
}: {
  reveal: RevealView;
  youId: string;
  busy: boolean;
  onWatchTogether: () => void;
}) {
  const by = reveal.off?.by;
  const who = by ? (by.id === youId ? 'You' : by.displayName) : null;
  const why =
    reveal.mode === 'done'
      ? null
      : reveal.off?.reason === 'absent'
        ? `${who ?? 'The driver'} stepped away, so everyone’s browsing on their own.`
        : `${who ?? 'Someone'} turned co-viewing off. Browse the comics at your own pace.`;

  if (!why) {
    // The show is over and ready-up has taken over; the host can still replay it for everyone.
    return reveal.youHost ? (
      <button type="button" className="link-button block" onClick={onWatchTogether} disabled={busy}>
        Watch this round’s comics together again
      </button>
    ) : null;
  }
  return (
    <div className="card card-soft solo-note">
      <p className="muted">{why}</p>
      {reveal.youHost ? (
        <button type="button" className="button primary" onClick={onWatchTogether} disabled={busy}>
          Watch together
        </button>
      ) : null}
    </div>
  );
}

function RoomCode({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const share = async () => {
    const url = `${window.location.origin}/?code=${code}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Panels', text: `Join my Panels room: ${code}`, url });
        return;
      } catch {
        // Cancelled the sheet — fall through to copying.
      }
    }
    if (await copyText(code)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };

  return (
    <button type="button" className="room-code" onClick={share}>
      <span className="room-code-label">{copied ? 'Copied' : 'Room code — tap to share'}</span>
      <span className="room-code-value mono">{code}</span>
    </button>
  );
}

function MemberList({
  members,
  creatorId,
  youId,
  showReady,
}: {
  members: MemberInfo[];
  creatorId: string;
  youId: string;
  /** Between rounds: show who is in for another. */
  showReady: boolean;
}) {
  return (
    <section className="section">
      <h2>
        Players <span className="muted">({members.filter((m) => m.active).length})</span>
      </h2>
      <ul className="list">
        {members.map((member) => {
          const notes = [
            member.id === creatorId ? 'host' : '',
            !member.active ? 'sitting out' : '',
            showReady && member.active ? (member.ready ? 'ready' : 'not ready yet') : '',
          ].filter(Boolean);
          const status: Status = !member.active ? 'idle' : showReady && !member.ready ? 'waiting' : 'ready';
          return (
            <li key={member.id} className="row static">
              <span className="row-main">
                <strong>
                  {member.displayName}
                  {member.id === youId ? ' (you)' : ''}
                </strong>
                <span className="muted">{notes.join(' · ')}</span>
              </span>
              <StatusDot status={status} />
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * Round 2 §2.3: everyone, host included, can say they're in for another. When
 * every active member is, the round starts by itself.
 */
function ReadyUp({
  members,
  youReady,
  busy,
  onReady,
}: {
  members: MemberInfo[];
  youReady: boolean;
  busy: boolean;
  onReady: (ready: boolean) => void;
}) {
  const readyCount = members.filter((m) => m.ready).length;
  return (
    <div className="ready-up">
      <button
        type="button"
        className={`button ${youReady ? 'is-ready' : 'primary'}`}
        onClick={() => onReady(!youReady)}
        disabled={busy}
        aria-pressed={youReady}
      >
        {youReady ? '✓ You’re in — tap to take it back' : 'I’m in for another'}
      </button>
      <p className="muted center small">
        {readyCount} of {members.length} ready
        {members.length >= MIN_PLAYERS ? ' — the next round starts when everyone is.' : '.'}
      </p>
    </div>
  );
}

/**
 * The waiting screen (§10, screen 5). Light social pressure is the whole
 * enforcement mechanism for an untimed game, so each player still drawing shows
 * how busy they are (Round 2 §1.1): a live count of drawing actions, and once
 * they go quiet, how long for. Skip appears only against someone who is away.
 */
function WaitingPanel({
  state,
  receivedAt,
  onSkip,
}: {
  state: RoomState;
  receivedAt: number;
  onSkip: (id: string) => void;
}) {
  const waiting = state.round?.waitingOn ?? [];
  const done = state.round?.doneThisPhase ?? [];
  const activity = new Map((state.round?.activity ?? []).map((a) => [a.playerId, a]));
  // Idle timers tick between polls.
  const now = useNow([...activity.values()].some((a) => a.status !== 'drawing'));
  const sincePoll = Math.max(0, now - receivedAt);

  return (
    <>
      <div className="waiting-hero">
        <StatusDot status="waiting" />
        <p>
          {waiting.length === 0
            ? 'Finishing up…'
            : waiting.length === 1
              ? `Waiting on ${waiting[0].displayName}`
              : `Waiting on ${waiting.length} people`}
        </p>
      </div>

      <section className="section">
        <h2>Still drawing</h2>
        <ul className="list">
          {waiting.map((player) => {
            const vote = state.skipVotes.find((v) => v.targetId === player.id);
            const act = activity.get(player.id);
            // Skip is only for someone who is away (red), never yourself.
            const showSkip = act?.status === 'away' && player.id !== state.you.id;
            return (
              <li key={player.id} className={`row static activity-${act?.status ?? 'drawing'}`}>
                <span className="row-main">
                  <strong>{player.displayName}</strong>
                  <span className="muted activity-line">
                    {act ? <ActivityLine activity={act} sincePoll={sincePoll} /> : 'drawing'}
                    {vote ? ` · skip vote ${vote.voters.length}/${vote.votesNeeded}` : ''}
                  </span>
                </span>
                <span className="row-actions">
                  <StatusDot status={activityDot(act)} label={activityLabel(act)} />
                  {showSkip ? (
                    <button
                      type="button"
                      className="button tiny"
                      onClick={() => onSkip(player.id)}
                      disabled={vote?.youVoted}
                    >
                      {vote?.youVoted ? 'Voted' : 'Skip'}
                    </button>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      </section>

      {done.length > 0 ? (
        <section className="section">
          <h2>Done</h2>
          <ul className="list">
            {done.map((player) => (
              <li key={player.id} className="row static">
                <span className="row-main">
                  <strong>{player.displayName}</strong>
                </span>
                <StatusDot status="ready" />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

const activityDot = (act: PlayerActivity | undefined): Status =>
  !act || act.status === 'drawing' ? 'ready' : act.status === 'quiet' ? 'waiting' : 'blocked';

const activityLabel = (act: PlayerActivity | undefined): string =>
  !act || act.status === 'drawing' ? 'Drawing' : act.status === 'quiet' ? 'Quiet' : 'Away';

/** "23 actions · idle 0:41". The timer only appears once they have gone quiet. */
function ActivityLine({ activity, sincePoll }: { activity: PlayerActivity; sincePoll: number }) {
  const count = `${activity.actionCount} ${activity.actionCount === 1 ? 'action' : 'actions'}`;
  if (activity.status === 'drawing') return <>{count}</>;
  if (activity.gone) return <>{count} · not looking</>;
  return (
    <>
      {count} · idle <span className="mono">{formatDuration(activity.idleMs + sincePoll)}</span>
    </>
  );
}

function formatDuration(msTotal: number): string {
  const total = Math.floor(msTotal / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = String(total % 60).padStart(2, '0');
  if (minutes < 60) return `${minutes}:${seconds}`;
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
}

function ShareRoomLink({ shareToken }: { shareToken: string }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/r/${shareToken}`;

  return (
    <button
      type="button"
      className="link-button block"
      onClick={async () => {
        if (await copyText(url)) {
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        }
      }}
    >
      {copied ? 'Gallery link copied' : 'Copy this room’s gallery link'}
    </button>
  );
}
